import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";

/**
 * EmailVerificationBanner – fast topp-stripe som vises når innlogget bruker
 * har email_verified=false. Knapp for å sende ny verifiserings-e-post.
 *
 * Brukeren kan dismisse banneret for en time (lagres i sessionStorage).
 */
const DISMISS_KEY = "ne_email_verif_dismissed_until";

export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const until = parseInt(sessionStorage.getItem(DISMISS_KEY) || "0", 10);
    return until > Date.now();
  });

  // Vis bare når innlogget OG eksplisitt email_verified=false (ikke undefined for
  // legacy-brukere før feltet eksisterte)
  if (!user || user.email_verified !== false || dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify-email/send");
      toast.success("Ny verifiserings-e-post sendt. Sjekk innboksen din.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke sende e-post");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now() + 60 * 60 * 1000));
    setDismissed(true);
  };

  return (
    <div
      className="w-full bg-[var(--magenta)]/15 border-b border-[var(--magenta)]/30 px-4 py-3"
      data-testid="email-verification-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <div className="flex items-start gap-3 flex-1">
          <Mail className="h-4 w-4 text-[var(--magenta)] shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--cream)] leading-relaxed">
            <strong>Bekreft e-posten din.</strong>
            <span className="text-[var(--cream-soft)]">
              {" "}Vi sendte en lenke til <span className="font-mono text-[var(--cream)]">{user.email}</span>.
              Du må bekrefte før du kan logge inn på nytt eller bruke alle funksjoner.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={resend}
            disabled={busy}
            className="px-3 py-1.5 text-[10px] font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40"
            data-testid="resend-verification-btn"
          >
            {busy ? "SENDER…" : "SEND PÅ NYTT"}
          </button>
          <button
            onClick={dismiss}
            className="p-1.5 text-[var(--cream-soft)] hover:text-[var(--cream)]"
            aria-label="Skjul"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
